/* eslint-disable camelcase */
import { useTranslation } from 'react-i18next';
import { getDeviceType } from '@app/utils';
import { useTokenUserData } from '@data/hooks';
import { AnswerType, MatchingTrialStatus, Role } from '@data/sdk/match';
import { useAppUserStore } from '@data/storage';
import { useTrackEvents } from '@data/utils/events';
import { pulse } from '@data/utils/pulse';

// Every action goes to GTM (marketing) and to Pulse (first-party BI). Page views have no
// Pulse twin here: useB2BPageTracking emits them from the route.
export const useB2BActivity = () => {
  const { i18n } = useTranslation();
  const { events, trackEvent } = useTrackEvents();
  const { user } = useAppUserStore();
  const { userId } = useTokenUserData();

  const getCommonAttributes = () => ({
    language: i18n.language,
    user_type: user?.currentRole,
    device_type: getDeviceType(),
    timestamp: new Date().toISOString(),
    userId,
  });

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const trackLoginSuccess = (userId: string, user_type: Role) => {
    trackEvent(events.TrialManagerLoginSuccess, {
      ...getCommonAttributes(),
      userId,
      user_type,
    });
    pulse.b2bAction({ action: 'login', userRole: user_type });
  };

  const trackTrailsListPageView = (
    filter_count: number,
    search_query: string,
    trial_count_displayed: number,
  ) => {
    trackEvent(events.TrialManagerTrialsListPageView, {
      ...getCommonAttributes(),
      filter_count,
      search_query,
      trial_count_displayed,
    });
  };

  const trackTrailDetailsPageView = (trial_id: string, trial_status: MatchingTrialStatus) => {
    trackEvent(events.TrialManagerTrialDetailsPageView, {
      ...getCommonAttributes(),
      trial_id,
      trial_status,
    });
  };

  const trackTrailEdit = (trial_id: string, edited_fields: string[]) => {
    trackEvent(events.TrialManagerTrialEdit, {
      ...getCommonAttributes(),
      trial_id,
      edited_fields: edited_fields.join(', '),
    });
    pulse.b2bAction({ action: 'trial_edit', trialId: trial_id, detail: edited_fields.join(',') });
  };

  const trackCriteriaEdit = (trial_id: string, criterium_id: string, edited_fields: string[]) => {
    trackEvent(events.TrialManagerCriteriaEdit, {
      ...getCommonAttributes(),
      trial_id,
      criterium_id,
      edited_fields: edited_fields.join(', '),
    });
    pulse.b2bAction({
      action: 'criteria_edit',
      trialId: trial_id,
      targetId: criterium_id,
      detail: edited_fields.join(','),
    });
  };

  const trackQuestionAdd = (
    trial_id: string,
    answer_type: AnswerType,
    question_text_length: number,
  ) => {
    trackEvent(events.TrialManagerAddNewTrialCriterium, {
      ...getCommonAttributes(),
      trial_id,
      answer_type,
      question_text_length,
    });
    pulse.b2bAction({ action: 'criterium_add', trialId: trial_id, detail: answer_type });
  };

  const trackSurveyCreate = (survey_id: string, question_count: number) => {
    trackEvent(events.TrialManagerSurveyCreate, {
      ...getCommonAttributes(),
      survey_id,
      question_count,
    });
    pulse.b2bAction({ action: 'survey_create', targetId: survey_id });
  };

  const trackPanelCreate = (panel_id: string, question_count: number) => {
    trackEvent(events.TrialManagerPanelCreate, {
      ...getCommonAttributes(),
      panel_id,
      question_count,
    });
    pulse.b2bAction({ action: 'panel_create', targetId: panel_id });
  };

  const trackSurveyLaunch = (survey_id: string) => {
    trackEvent(events.TrialManagerSurveyLaunch, {
      ...getCommonAttributes(),
      survey_id,
    });
    pulse.b2bAction({ action: 'survey_launch', targetId: survey_id });
  };

  const trackPanelLaunch = (panel_id: string) => {
    trackEvent(events.TrialManagerPanelLaunch, {
      ...getCommonAttributes(),
      panel_id,
    });
    pulse.b2bAction({ action: 'panel_launch', targetId: panel_id });
  };

  const trackSurveyViewed = (survey_id: string) => {
    trackEvent(events.TrialManagerSurveyViewed, {
      ...getCommonAttributes(),
      survey_id,
    });
    pulse.b2bAction({ action: 'survey_viewed', targetId: survey_id });
  };

  const trackPanelViewed = (panel_id: string) => {
    trackEvent(events.TrialManagerPanelViewed, {
      ...getCommonAttributes(),
      panel_id,
    });
    pulse.b2bAction({ action: 'panel_viewed', targetId: panel_id });
  };

  return {
    trackLoginSuccess,
    trackTrailsListPageView,
    trackTrailDetailsPageView,
    trackTrailEdit,
    trackCriteriaEdit,
    trackQuestionAdd,
    trackSurveyCreate,
    trackPanelCreate,
    trackSurveyLaunch,
    trackPanelLaunch,
    trackSurveyViewed,
    trackPanelViewed,
  };
};
